import React, { useState } from 'react'
import Avatar from '../../components/Avatar/Avatar'
import StyledText from '../../components/StyledComponents/StyledText'
import EditIcon from '../../assets/icons/EditIcon'
import CustomInput from '../../components/form/CustomInput'
import LoadingCircleIcon from '../../assets/icons/LoadingCircleIcon'
import TickIcon from '../../assets/icons/TickIcon'
import CrossIcon from '../../assets/icons/CrossIcon'
import { InfoBox } from '../contacts/ContactPreview'
import { useTheme } from '../../middleware/ThemeContext'
import { ContactOrganization, OrganizationProps } from '../../types/types'
import CopyIcon from '../../assets/icons/CopyIcon'
import NewCopyIcon from '../../assets/icons/NewCopyIcon'
import { copyToClipboard, formatDateDefault } from '../../utils/helper'
import NewEmailIcon from '../../assets/icons/NewEmailIcon'
import WebsiteIcon from '../../assets/icons/WebsiteIcon'
import InfoIcon from '../../assets/icons/InfoIcon'
import styled from 'styled-components'
import { Theme } from '../../utils/themes'
import moment from 'moment'

export type FieldDataType =
    | 'NUMBERS'
    | 'SELECT'
    | 'SINGLE_LINE';

export interface RecordField {
    fieldId: number;
    fieldName: string;
    accessKey: string;
    dataType: FieldDataType;
    value: string | number | null;
}

export interface RecordItem {
    recordId: number;
    fields: RecordField[];
}

export type RecordsResponse = RecordItem[];


interface Props {
    organization: OrganizationProps,
    handleUpload: (file: File) => Promise<string>,
    updateName: () => void,
    editName: boolean,
    seteditName: React.Dispatch<React.SetStateAction<boolean>>,
    setOrganizationName: React.Dispatch<React.SetStateAction<string | undefined>>,
    isLoading: boolean,
    loading: boolean,
    organizationName?: string,
    isLoadingName: boolean,
    updateOrgName?: () => void,
    setShowAboutOrgModal: React.Dispatch<React.SetStateAction<boolean>>,
    organizationData?: RecordsResponse
}

const OrganizationHeader = ({ organizationData, setShowAboutOrgModal, organization, handleUpload, updateOrgName, updateName, editName, seteditName, setOrganizationName, isLoading, loading, organizationName, isLoadingName }: Props) => {
    const { theme } = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!organization?.id) return;

        await copyToClipboard(organization?.id);
        setCopied(true);

        setTimeout(() => setCopied(false), 2000); // 2 seconds baad icon wapis
    };

    const aboutOrganization = organizationData?.[0];
    const fields = aboutOrganization?.fields ?? [];
    // const PrimaryOrganization = organization?.contactOrganizations?.find((org: ContactOrganization) => org.isPrimary);

    const orgEmail = fields.find((field) => field.accessKey === "organization_email")?.value;
    const orgWebsite = fields.find((field) => field.accessKey === "organization_website")?.value;

    const formatFieldValue = (field: any) => {
        const { dataType, value } = field;

        if (!value) return "-";

        switch (dataType) {
            case "NUMBERS":
                return value;

            case "SELECT":
                return value?.label ?? value;

            case "SINGLE_LINE":
                return value;

            case "DATE":
                return new Date(value).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                });

            default:
                return value;
        }
    };

    const FIELD_ORDER_CONFIG = [
        {
            accessKey: 'organization_website',
            label: 'Website',
        },
        {
            accessKey: 'organization_industry',
            label: 'Industry',
        },
        {
            accessKey: 'organization_region',
            label: 'Region',
        },
        {
            accessKey: 'organization_number_of_employees',
            label: 'Employee Range',
        },
    ] as const;


    const sortedFields = FIELD_ORDER_CONFIG
        .map((config) => {
            const field = fields.find(
                (f) => f.accessKey === config.accessKey
            );

            if (!field) return null;

            return {
                ...field,
                displayLabel: config.label,
            };
        })
        .filter(Boolean);



    return (

        <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex gap-3 align-items-center">
                <Avatar
                    name={organization?.name || "Organization"}
                    imageUrl={organization?.signedUrl}
                    size={150}
                    border={"5px solid #FFFFFF"}
                    enableUpload
                    onUpload={handleUpload}
                    handleUpdate={updateName}
                    loading={isLoading || loading}
                    type="organization"
                />

                <div>
                    {!editName ? (
                        <div className="d-flex gap-3 align-items-center">
                            <StyledText
                                fontSize="28px"
                                fontWeight={700}
                                textColor={theme.CorporateTheme?.colors?.primary}
                            >
                                {organization?.name || "Organization Name"}
                            </StyledText>
                            <EditIcon
                                className="cursor-pointer"
                                color={theme.CorporateTheme.colors.gunmetal}
                                onClick={() => seteditName(true)}
                                size={18}
                            />
                            <IdBadge theme={theme} className='d-flex gap-1 align-items-center p-1 rounded-1 cursor-pointer' onClick={handleCopy}>
                                <StyledText
                                    fontSize="12px"
                                    fontWeight={500}
                                    textColor={theme.CorporateTheme?.colors?.heavyMetal}
                                >
                                    {organization?.id}
                                </StyledText>
                                {copied ? (
                                    <TickIcon size={14} color={theme.CorporateTheme.colors.green} />
                                ) : (
                                    <NewCopyIcon size={14} color={theme.CorporateTheme?.colors?.heavyMetal} />
                                )}
                            </IdBadge>
                        </div>
                    ) : (
                        <div className="d-flex gap-3 align-items-center">
                            <div className="d-flex">
                                <CustomInput
                                    value={organizationName || ""}
                                    onChange={(value) => setOrganizationName(value)}
                                    placeholder={"First Name"}
                                    type="text"
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2 ">
                                {isLoadingName ? (
                                    <LoadingCircleIcon
                                        color={theme.CorporateTheme.colors.secondary}
                                    />
                                ) : (
                                    <TickIcon
                                        className="cursor-pointer"
                                        onClick={updateOrgName}
                                    />
                                )}
                                <CrossIcon
                                    className={`${isLoadingName && "class-disable"
                                        } cursor-pointer`}
                                    onClick={() => seteditName(false)}
                                    color={theme.CorporateTheme.colors.textSecondary}
                                />
                            </div>
                            <IdBadge theme={theme} className='d-flex gap-1 align-items-center p-1 rounded-1 cursor-pointer' onClick={handleCopy}>
                                <StyledText
                                    fontSize="12px"
                                    fontWeight={500}
                                    textColor={theme.CorporateTheme?.colors?.heavyMetal}
                                >
                                    {organization?.id}
                                </StyledText>
                                {copied ? (
                                    <TickIcon size={14} color={theme.CorporateTheme.colors.green} />
                                ) : (
                                    <NewCopyIcon size={14} color={theme.CorporateTheme?.colors?.heavyMetal} />
                                )}
                            </IdBadge>
                        </div>
                    )}
                    {orgEmail &&
                    <div className='d-flex gap-2 align-items-center mt-2'>
                        <NewEmailIcon color={theme.CreateFormTheme.colors.primary} size={20} />
                        <StyledText
                            fontSize="16px"
                            fontWeight={400}
                            textColor={theme.CorporateTheme?.colors?.textSecondary}
                        >
                            {orgEmail}
                        </StyledText>
                    </div>}
                    {orgWebsite &&
                    <div className='d-flex gap-2 align-items-center mt-1'>
                        <WebsiteIcon color={theme.CreateFormTheme.colors.primary} size={20} />
                        <StyledText
                            fontSize="16px"
                            fontWeight={400}
                            textColor={theme.CorporateTheme?.colors?.textSecondary}
                        >
                            {orgWebsite}
                        </StyledText>
                    </div>
                    }
                </div>
            </div>

            <div className="d-flex gap-3">
                {/* <div
                    className="d-flex flex-column p-3 px-4 rounded-1 py-3 "
                    style={{ backgroundColor: theme.colors.contentBackground }}
                >
                    <div className="d-flex justify-content-between gap-5 align-items-center mb-3">
                        <StyledText
                            fontSize="16px"
                            fontWeight={700}
                            textColor={theme.CorporateTheme?.colors?.headerColor}

                        >
                            About this Organization
                        </StyledText>
                        <EditIcon size={18} color={theme.CorporateTheme.colors.gunmetal} className='cursor-pointer' onClick={() => setShowAboutOrgModal(true)} />
                    </div>
                    <div className="d-flex flex-column mt-2 gap-3 ">
                        {sortedFields.map((field) => (
                            <div key={field?.fieldId} className="d-flex gap-2">
                                <StyledText
                                    key={field?.fieldId}
                                    fontSize="14px"
                                    fontWeight={600}
                                    textColor={theme.CorporateTheme?.colors?.textSecondary}
                                >
                                    {field?.displayLabel}:
                                </StyledText>
                                <StyledText
                                    key={field?.fieldId}
                                    fontSize="14px"
                                    fontWeight={400}
                                    textColor={theme.CorporateTheme?.colors?.textSecondary}
                                >
                                    {formatFieldValue(field)}
                                </StyledText>
                            </div>
                        ))}
                    </div>
                </div> */}

                <div
                    className="d-flex flex-column p-3 px-4 rounded-1 py-3"
                    style={{ backgroundColor: 'transparent', height: '100%' }}
                >
                    <StyledText
                        fontSize="16px"
                        fontWeight={700}
                        textColor={theme.CorporateTheme?.colors?.headerColor}
                        style={{ marginBottom: "15px" }}
                    >
                        Creation Detail
                    </StyledText>
                    <div className="d-flex flex-column mt-2 gap-1 ">
                        {organization?.account || organization?.createdBy ? (
                            <StyledText
                                fontSize="14px"
                                fontWeight={400}
                                textColor={theme.CorporateTheme?.colors?.textSecondary}
                                style={{ marginBottom: "10px" }}
                            >
                                <strong>Created By: </strong>
                                {organization?.account
                                    ? organization?.account?.firstName +
                                    " " +
                                    organization?.account?.lastName
                                    : organization?.createdBy?.firstName +
                                    " " +
                                    organization?.createdBy?.lastName}
                            </StyledText>
                        ) : null}
                        <StyledText
                            fontSize="14px"
                            fontWeight={400}
                            textColor={theme.CorporateTheme?.colors?.textSecondary}
                            style={{ marginBottom: "10px" }}
                        >
                            <strong>Creation Date: </strong>
                            {/* {new Date(
                                organization?.createdAt ?? ""
                            ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })} */}
                            {
                            organization?.createdAt ?
                            formatDateDefault(organization?.createdAt, true) : '-'
                            }
                        </StyledText>
                        <StyledText
                            fontSize="14px"
                            fontWeight={400}
                            textColor={theme.CorporateTheme?.colors?.textSecondary}
                            style={{ marginBottom: "10px" }}
                        >
                            <strong>Last Modified By: </strong>
                            {organization?.lastModifiedByAccount ? organization?.lastModifiedByAccount?.firstName + " " + organization?.lastModifiedByAccount?.lastName : "-"}
                        </StyledText>
                        <StyledText
                            fontSize="14px"
                            fontWeight={400}
                            textColor={theme.CorporateTheme?.colors?.textSecondary}
                            style={{ marginBottom: "10px" }}
                        >
                            <strong>Last Modified Date: </strong>
                            {organization?.lastModifiedByAccount && organization?.updatedAt ? 
                            formatDateDefault(organization?.updatedAt, true)
                             : "-"}
                            {/* {new Date(organization?.updatedAt ?? "").toLocaleString(
                                "en-US",
                                {
                                    hour: "numeric",
                                    minute: "numeric",
                                    hour12: true,
                                }
                            )} */}
                        </StyledText>
                        <InfoBox theme={theme}>
                            <InfoIcon color={theme.CorporateTheme.colors.primary} />
                            <StyledText
                                fontSize="14px"
                                fontWeight={500}
                                textColor={theme.CorporateTheme.colors.headerColor}
                                style={{ fontStyle: "italic" }}
                                htmlTag={"span"}
                            >
                                Source: {organization?.source}
                            </StyledText>
                        </InfoBox>
                    </div>
                </div>
            </div>
        </div>
    )
}


export const IdBadge = styled.div<{ theme: Theme }>`
//   background: ${({ theme }) => theme.colors.contentBackground};
`;

export default OrganizationHeader
